<template>
  <div class="hr-page user-list-page">
    <header class="console-page-head">
      <div class="console-page-copy">
        <p class="console-kicker">조직</p>
        <h2 class="console-title">인사관리</h2>
      </div>
      <div class="console-head-side">
        <span class="console-count">직원 {{ totalCount }}명</span>
        <v-btn v-if="isAdmin" size="small" variant="flat" class="btn-register" @click="openCreateDialog">
          <v-icon size="small" class="mr-1">mdi-plus</v-icon>
          유저 등록
        </v-btn>
      </div>
    </header>

    <div class="hr-body">
    <div class="hr-main-layout">

        <div class="content-card">
          <div class="user-filter">
            <label class="user-filter-search">
              <v-icon size="18">mdi-magnify</v-icon>
              <input
                v-model="searchText"
                type="text"
                placeholder="이름, 아이디, 이메일"
                @keydown.enter="searchUsers"
              />
              <button v-if="searchText" type="button" class="user-filter-clear" @click="onClearSearch">지우기</button>
            </label>
            <label class="user-filter-role">
              <span>권한</span>
              <select v-model="selectedRoleCode" @change="searchUsers">
                <option v-for="role in roleOptions" :key="role.role_code || 'all'" :value="role.role_code">
                  {{ role.role_name }}
                </option>
              </select>
            </label>
            <div class="user-filter-status">
              <button
                v-for="opt in statusOptions"
                :key="String(opt.value)"
                type="button"
                :class="{ active: selectedStatus === opt.value }"
                @click="setStatus(opt.value)"
              >
                {{ opt.text }}
              </button>
            </div>
          </div>

          <v-data-table
            :headers="headers"
            :items="userList"
            :loading="loading"
            :items-per-page="-1"
            hover
            class="user-table"
            :row-props="rowProps"
            @click:row="onRowClick"
          >
            <template v-slot:item.index="{ index }">
              <span class="row-num">{{ (currentPage - 1) * itemsPerPage + index + 1 }}</span>
            </template>

            <template v-slot:item.u_name="{ item }">
              <div class="user-name-cell">
                <div class="avatar" :class="getRoleClass(item.role_level)">{{ item.u_name?.charAt(0) }}</div>
                <div class="info">
                  <span class="name-text">{{ item.u_name }}</span>
                  <span class="id-text">{{ item.u_id }}</span>
                </div>
              </div>
            </template>

            <template v-slot:item.org_info="{ item }">
              <span class="org-text">{{ item.org_name || '—' }}</span>
            </template>

            <template v-slot:item.role_name="{ item }">
              <span class="role-pill">
                <span class="dot" :style="{ background: getRoleColor(item.u_role_code) }"></span>
                {{ item.role_name || '—' }}
              </span>
            </template>

            <template v-slot:item.contact="{ item }">
              <div class="contact-info">
                <span v-if="item.u_phone" class="phone">{{ item.u_phone }}</span>
                <span v-if="item.u_email" class="email">{{ item.u_email }}</span>
                <span v-if="!item.u_phone && !item.u_email" class="empty">연락처 없음</span>
              </div>
            </template>

            <template v-slot:item.u_status="{ item }">
              <span class="user-state" :class="item.u_status === 1 ? 'is-on' : 'is-off'">
                {{ item.u_status === 1 ? '활성' : '비활성' }}
              </span>
            </template>

            <template v-slot:item.u_last_login="{ item }">
              <span class="last-login" :class="{ recent: isRecentLogin(item.u_last_login) }">
                {{ formatDateTime(item.u_last_login) }}
              </span>
            </template>

            <template v-slot:item.actions="{ item }">
              <div class="action-btns" @click.stop>
                <button
                  v-if="isAdmin && canImpersonateUser && item.u_idx !== currentUser?.idx"
                  type="button"
                  class="row-action"
                  @click="doImpersonate(item)"
                >
                  접속
                </button>
                <button type="button" class="row-action" @click="openEdit(item)">수정</button>
              </div>
            </template>

            <template v-slot:no-data>
              <div class="empty-message">
                <p>등록된 직원이 없습니다.</p>
              </div>
            </template>

            <template v-slot:bottom>
              <TablePagination
                v-model:current-page="currentPage"
                v-model:items-per-page="itemsPerPage"
                :total-count="totalCount"
                :total-pages="totalPages"
                @update:current-page="onPageChange"
                @update:items-per-page="onItemsPerPageChange"
              />
            </template>
          </v-data-table>
        </div>

    </div><!-- /hr-main-layout -->
    </div><!-- /hr-body -->

    <v-dialog v-model="editDialog" max-width="520px" persistent>
      <v-card class="user-edit-dialog">
        <div class="user-edit-head">
          <div class="user-edit-head-text">
            <h3>직원 수정</h3>
            <p v-if="editForm.u_id">{{ editForm.u_id }}</p>
          </div>
          <button
            type="button"
            class="user-edit-dialog__close"
            :disabled="editSaving"
            aria-label="닫기"
            @click="editDialog = false"
          >
            <v-icon size="18">mdi-close</v-icon>
          </button>
        </div>
        <div v-if="editLoading" class="user-edit-loading">불러오는 중</div>
        <form v-else class="user-edit-form" @submit.prevent="saveEdit">
          <label class="console-field">
            <span>이름</span>
            <input v-model="editForm.u_name" type="text" :disabled="!canSaveEdit" />
          </label>
          <label class="console-field">
            <span>이메일</span>
            <input v-model="editForm.u_email" type="text" :disabled="!canSaveEdit" />
          </label>
          <label class="console-field">
            <span>연락처</span>
            <input v-model="editForm.u_phone" type="text" :disabled="!canSaveEdit" />
          </label>
          <div class="user-edit-split">
            <label class="console-field">
              <span>권한</span>
              <select v-model="editForm.u_role_code" :disabled="!canChangeRole">
                <option v-for="role in editRoleOptions" :key="role.role_code" :value="role.role_code">
                  {{ role.role_name }}
                </option>
              </select>
            </label>
            <label class="console-field">
              <span>상태</span>
              <select v-model.number="editForm.u_status" :disabled="!canChangeRole">
                <option :value="1">활성</option>
                <option :value="0">비활성</option>
              </select>
            </label>
          </div>
          <label class="console-field">
            <span>비밀번호</span>
            <input v-model="editForm.u_pw" type="password" placeholder="바꾸지 않으면 비워 두세요" :disabled="!canSaveEdit" />
          </label>
          <div class="user-edit-actions">
            <button
              v-if="canDeleteEdit"
              type="button"
              class="user-edit-delete"
              :disabled="editSaving || deleteLoading"
              @click="confirmDelete(editForm)"
            >
              삭제
            </button>
            <span class="user-edit-spacer"></span>
            <button type="button" class="user-edit-cancel" :disabled="editSaving" @click="editDialog = false">취소</button>
            <button type="submit" class="user-edit-save" :disabled="!canSaveEdit || editSaving">
              {{ editSaving ? '저장 중' : '저장' }}
            </button>
          </div>
        </form>
      </v-card>
    </v-dialog>

    <!-- 유저 생성 다이얼로그 -->
    <v-dialog v-model="createDialog" max-width="500px" persistent>
      <v-card class="create-dialog">
        <v-card-title class="dialog-header-simple">
          <v-icon class="mr-2">mdi-account-plus</v-icon>
          유저 등록
          <button
            type="button"
            class="create-dialog__close"
            :disabled="createLoading"
            aria-label="닫기"
            @click="createDialog = false"
          >
            <v-icon size="18">mdi-close</v-icon>
          </button>
        </v-card-title>
        <v-card-text class="create-dialog-body">
          <v-form @submit.prevent="createUser">
            <v-row dense>
              <v-col cols="6">
                <v-text-field
                  v-model="newUser.u_id"
                  label="아이디 *"
                  variant="outlined"
                  density="compact"
                  :rules="[v => !!v || '아이디를 입력하세요']"
                />
              </v-col>
              <v-col cols="6">
                <v-text-field
                  v-model="newUser.u_pw"
                  label="비밀번호 *"
                  type="password"
                  variant="outlined"
                  density="compact"
                  :rules="[v => !!v || '비밀번호를 입력하세요']"
                />
              </v-col>
              <v-col cols="6">
                <v-text-field
                  v-model="newUser.u_name"
                  label="이름 *"
                  variant="outlined"
                  density="compact"
                  :rules="[v => !!v || '이름을 입력하세요']"
                />
              </v-col>
              <v-col cols="6">
                <v-select
                  v-model="newUser.u_role_code"
                  :items="roleList"
                  item-title="role_name"
                  item-value="role_code"
                  label="권한 *"
                  variant="outlined"
                  density="compact"
                >
                  <template v-slot:item="{ props, item }">
                    <v-list-item v-bind="props">
                      <template v-slot:prepend>
                        <span class="color-dot" :style="{ background: item.raw.role_color }"></span>
                      </template>
                    </v-list-item>
                  </template>
                </v-select>
              </v-col>
              <v-col cols="6">
                <v-text-field
                  v-model="newUser.u_email"
                  label="이메일"
                  variant="outlined"
                  density="compact"
                />
              </v-col>
              <v-col cols="6">
                <v-text-field
                  v-model="newUser.u_phone"
                  label="전화번호"
                  variant="outlined"
                  density="compact"
                />
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions class="dialog-actions">
          <v-spacer />
          <v-btn variant="text" class="btn-cancel" @click="createDialog = false" :disabled="createLoading">취소</v-btn>
          <v-btn variant="flat" class="btn-register" @click="createUser" :loading="createLoading">등록</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- 삭제 확인 다이얼로그 -->
    <DeleteConfirmDialog
      v-model="deleteDialog"
      title="유저 삭제"
      :target-name="`${deleteTarget?.u_name || ''} (${deleteTarget?.u_id || ''})`"
      message="유저를 삭제하시겠습니까?"
      :loading="deleteLoading"
      @confirm="deleteUser"
    />
  </div><!-- /hr-page -->
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { userApi, authApi } from '@/services/api';
import TablePagination from '@/components/common/TablePagination.vue';
import DeleteConfirmDialog from '@/components/common/DeleteConfirmDialog.vue';

const store = useStore();
const router = useRouter();

const currentUser = computed(() => store.getters.currentUser);
const userOrgCode = computed(() => store.getters.orgCode);
const userRoleLevel = computed(() => store.getters.roleLevel);
const actualRoleLevel = computed(() => store.getters.actualRoleLevel);
const canImpersonateUser = computed(() => store.getters.canImpersonateUser);

// 관리자(60+) 여부 - 유저 등록/삭제, 대리 로그인, 상세 통계 표시 여부 결정
const isAdmin = computed(() => actualRoleLevel.value >= 60);

// 해당 유저를 수정할 수 있는지 확인 (같거나 높은 권한은 수정 불가, 어드민 90+는 제한 없음)
const canEditUser = (item: any) => {
  if (actualRoleLevel.value >= 90) return true;
  return (item.role_level || 0) < actualRoleLevel.value;
};

// 상태
const loading = ref(false);
const userList = ref<any[]>([]);
const totalCount = ref(0);
const currentPage = ref(1);
const itemsPerPage = ref(50);
const searchText = ref('');
const selectedRoleCode = ref('');
const selectedStatus = ref<number | string>(1); // 기본값: 활성
const roleList = ref<any[]>([]);

// 수정 가능한 권한 목록 (자기보다 낮은 권한만)
const editableRoleList = computed(() => {
  if (actualRoleLevel.value >= 90) return roleList.value;
  return roleList.value.filter((r: any) => (r.role_level || 0) < actualRoleLevel.value);
});

// 권한 옵션 (전체 포함)
const roleOptions = computed(() => {
  const options = [{ role_name: '전체', role_code: '', role_color: '' }];
  return options.concat(roleList.value);
});

// 적용된 필터 개수
const filterCount = computed(() => {
  let count = 0;
  if (selectedRoleCode.value) count++;
  if (selectedStatus.value !== '') count++;
  if (searchText.value) count++;
  return count;
});

// 필터 적용 여부
const hasActiveFilters = computed(() => filterCount.value > 0);

// 권한명 가져오기
const getRoleName = (roleCode: string) => {
  const role = roleList.value.find(r => r.role_code === roleCode);
  return role?.role_name || roleCode;
};
const editDialog = ref(false);
const editLoading = ref(false);
const editSaving = ref(false);
const editForm = ref({
  u_idx: 0,
  u_id: '',
  u_name: '',
  u_email: '',
  u_phone: '',
  u_role_code: '',
  u_status: 1,
  role_level: 0,
  u_pw: ''
});

const editingSelf = computed(() => editForm.value.u_idx === currentUser.value?.idx);
const canSaveEdit = computed(() => canEditUser(editForm.value) || editingSelf.value);
const canChangeRole = computed(() => canEditUser(editForm.value) && !editingSelf.value);
const canDeleteEdit = computed(() => isAdmin.value && canEditUser(editForm.value) && !editingSelf.value);
const editRoleOptions = computed(() => {
  const current = roleList.value.find((role: any) => role.role_code === editForm.value.u_role_code);
  const options = editableRoleList.value.slice();
  if (current && !options.some((role: any) => role.role_code === current.role_code)) {
    options.unshift(current);
  }
  return options;
});

// 로그인 기록
const loginHistory = ref<any[]>([]);
const loginHistoryLoading = ref(false);

// 인라인 편집
const editingField = ref<string | null>(null);
const editingUser = ref<any>(null);
const editValue = ref<any>('');

// 유저 생성/삭제
const createDialog = ref(false);
const deleteDialog = ref(false);
const deleteTarget = ref<any>(null);

const newUser = ref({
  u_id: '',
  u_pw: '',
  u_name: '',
  u_email: '',
  u_phone: '',
  u_org_code: null as string | null,
  u_role_code: 'MANAGER',
  u_status: 1
});
const createLoading = ref(false);
const deleteLoading = ref(false);

const statusOptions = [
  { text: '전체', value: '' },
  { text: '활성', value: 1 },
  { text: '비활성', value: 0 }
];

const headers = [
  { title: 'No', key: 'index', sortable: false, align: 'center' as const, width: '56px' },
  { title: '이름', key: 'u_name', sortable: false, width: '200px' },
  { title: '소속', key: 'org_info', sortable: false, width: '140px' },
  { title: '권한', key: 'role_name', sortable: false, width: '120px' },
  { title: '연락처', key: 'contact', sortable: false },
  { title: '상태', key: 'u_status', sortable: false, align: 'center' as const, width: '80px' },
  { title: '최근 로그인', key: 'u_last_login', sortable: false, width: '120px' },
  { title: '', key: 'actions', sortable: false, align: 'end' as const, width: '160px' }
];

const totalPages = computed(() => Math.ceil(totalCount.value / itemsPerPage.value));

// 유저 목록 조회
const fetchUserList = async () => {
  loading.value = true;
  try {
    const response = await userApi.getList({
      search: searchText.value || undefined,
      roleCode: selectedRoleCode.value || undefined,
      status: selectedStatus.value !== '' ? selectedStatus.value : undefined,
      orgCode: userOrgCode.value,
      roleLevel: userRoleLevel.value,
      page: currentPage.value,
      limit: itemsPerPage.value
    });

    if (response.data.success) {
      userList.value = response.data.data;
      totalCount.value = response.data.pagination.total;
    }
  } catch (error) {
    console.error('유저 목록 조회 오류:', error);
    store.dispatch('showError', '유저 목록을 불러오는데 실패했습니다.');
  } finally {
    loading.value = false;
  }
};

// 권한 목록 조회
const fetchRoleList = async () => {
  try {
    const response = await authApi.getRoles();
    if (response.data.success) {
      roleList.value = response.data.data;
    }
  } catch (error) {
    console.error('권한 목록 조회 오류:', error);
  }
};

const clearRoleFilter = () => {
  selectedRoleCode.value = '';
  currentPage.value = 1;
  fetchUserList();
};

const clearStatusFilter = () => {
  selectedStatus.value = '';
  currentPage.value = 1;
  fetchUserList();
};

const clearSearchFilter = () => {
  searchText.value = '';
  currentPage.value = 1;
  fetchUserList();
};

const clearAllFilters = () => {
  selectedRoleCode.value = '';
  selectedStatus.value = '';
  searchText.value = '';
  currentPage.value = 1;
  fetchUserList();
};

const onClearSearch = () => {
  searchText.value = '';
  currentPage.value = 1;
  fetchUserList();
};

const searchUsers = () => {
  currentPage.value = 1;
  fetchUserList();
};

const setStatus = (value: number | string) => {
  if (selectedStatus.value === value) return;
  selectedStatus.value = value;
  searchUsers();
};

const rowProps = (data: { item: any }) => ({
  class: data.item.u_status !== 1 ? 'is-inactive' : ''
});

const onRowClick = (_event: Event, row: { item: any }) => {
  openEdit(row.item);
};

const onPageChange = () => fetchUserList();
const onItemsPerPageChange = () => {
  currentPage.value = 1;
  fetchUserList();
};

const openEdit = async (user: any) => {
  editDialog.value = true;
  editLoading.value = true;
  editForm.value = {
    u_idx: user.u_idx,
    u_id: user.u_id || '',
    u_name: user.u_name || '',
    u_email: user.u_email || '',
    u_phone: user.u_phone || '',
    u_role_code: user.u_role_code || '',
    u_status: user.u_status ?? 1,
    role_level: user.role_level || 0,
    u_pw: ''
  };
  try {
    const response = await userApi.getById(user.u_idx);
    if (response.data.success) {
      const data = response.data.data;
      editForm.value = {
        u_idx: data.u_idx,
        u_id: data.u_id || '',
        u_name: data.u_name || '',
        u_email: data.u_email || '',
        u_phone: data.u_phone || '',
        u_role_code: data.u_role_code || '',
        u_status: data.u_status ?? 1,
        role_level: data.role_level || 0,
        u_pw: ''
      };
    }
  } catch (error) {
    store.dispatch('showError', '직원 정보를 불러오지 못했습니다.');
    editDialog.value = false;
  } finally {
    editLoading.value = false;
  }
};

const saveEdit = async () => {
  if (!canSaveEdit.value || !editForm.value.u_name.trim()) {
    store.dispatch('showError', '이름을 입력하세요.');
    return;
  }
  editSaving.value = true;
  try {
    const payload: Record<string, unknown> = {
      u_name: editForm.value.u_name.trim(),
      u_email: editForm.value.u_email.trim(),
      u_phone: editForm.value.u_phone.trim()
    };
    if (canChangeRole.value) {
      payload.u_role_code = editForm.value.u_role_code;
      payload.u_status = editForm.value.u_status;
    }
    if (editForm.value.u_pw.trim()) payload.u_pw = editForm.value.u_pw.trim();
    await userApi.update(editForm.value.u_idx, payload, actualRoleLevel.value);
    store.dispatch('showSuccess', '저장되었습니다.');
    editDialog.value = false;
    fetchUserList();
  } catch (error: any) {
    store.dispatch('showError', error.response?.data?.message || '저장에 실패했습니다.');
  } finally {
    editSaving.value = false;
  }
};

const formatDate = (dateStr: string) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

const formatDateTime = (dateStr: string) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  
  if (days === 0) return '오늘 ' + `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
  if (days === 1) return '어제';
  if (days < 7) return `${days}일 전`;
  
  return formatDate(dateStr);
};

const isRecentLogin = (dateStr: string) => {
  if (!dateStr) return false;
  const diff = new Date().getTime() - new Date(dateStr).getTime();
  return diff < 24 * 60 * 60 * 1000; // 24시간 이내
};

// 전체 날짜/시간 포맷 (로그인 기록용)
const formatFullDateTime = (dateStr: string) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  const h = String(date.getHours()).padStart(2, '0');
  const min = String(date.getMinutes()).padStart(2, '0');
  const s = String(date.getSeconds()).padStart(2, '0');
  return `${y}-${m}-${d} ${h}:${min}:${s}`;
};

// 로그인 기록 로드
const loadLoginHistory = async (user: any) => {
  loginHistoryLoading.value = true;
  loginHistory.value = [];
  
  try {
    const response = await authApi.getRecentLogins('user', user.u_idx, 10);
    if (response.data.success) {
      loginHistory.value = response.data.data;
    }
  } catch (error) {
    console.error('로그인 기록 조회 오류:', error);
  } finally {
    loginHistoryLoading.value = false;
  }
};

const getRoleColor = (roleCode: string) => {
  const role = roleList.value.find(r => r.role_code === roleCode);
  return role?.role_color || '#9e9e9e';
};

const getRoleClass = (roleLevel: number) => {
  if (roleLevel >= 90) return 'super';
  if (roleLevel >= 60) return 'admin';
  if (roleLevel >= 50) return 'manager';
  return 'staff';
};

// 필드 저장 (인라인 편집)
const saveUserField = async (user: any, field: string) => {
  try {
    let updateData: any = {};
    
    if (field === 'contact') {
      updateData = { u_email: user.u_email, u_phone: user.u_phone };
    } else {
      updateData = { [field]: user[field] };
    }
    
    await userApi.update(user.u_idx, updateData, actualRoleLevel.value);
    store.dispatch('showSuccess', '저장되었습니다.');
    fetchUserList();
  } catch (error: any) {
    console.error('저장 오류:', error);
    store.dispatch('showError', error.response?.data?.message || '저장에 실패했습니다.');
  }
};

// 유저 생성 다이얼로그 열기
const openCreateDialog = () => {
  newUser.value = {
    u_id: '',
    u_pw: '',
    u_name: '',
    u_email: '',
    u_phone: '',
    u_org_code: null,
    u_role_code: 'MANAGER',
    u_status: 1
  };
  createDialog.value = true;
};

// 유저 생성
const createUser = async () => {
  if (!newUser.value.u_id || !newUser.value.u_pw || !newUser.value.u_name) {
    store.dispatch('showError', '필수 항목을 입력해주세요.');
    return;
  }
  
  createLoading.value = true;
  try {
    await userApi.create(newUser.value);
    store.dispatch('showSuccess', '유저가 등록되었습니다.');
    createDialog.value = false;
    fetchUserList();
  } catch (error: any) {
    console.error('유저 생성 오류:', error);
    store.dispatch('showError', error.response?.data?.message || '유저 등록에 실패했습니다.');
  } finally {
    createLoading.value = false;
  }
};

// 삭제 확인
const confirmDelete = (user: any) => {
  deleteTarget.value = user;
  deleteDialog.value = true;
};

// 유저 삭제
const deleteUser = async () => {
  if (!deleteTarget.value) return;
  
  deleteLoading.value = true;
  try {
    await userApi.delete(deleteTarget.value.u_idx, actualRoleLevel.value);
    store.dispatch('showSuccess', '유저가 삭제되었습니다.');
    deleteDialog.value = false;
    deleteTarget.value = null;
    editDialog.value = false;
    fetchUserList();
  } catch (error: any) {
    console.error('유저 삭제 오류:', error);
    store.dispatch('showError', error.response?.data?.message || '유저 삭제에 실패했습니다.');
  } finally {
    deleteLoading.value = false;
  }
};

// 대리 로그인 실행 (바로 접속)
const doImpersonate = (target: any) => {
  const role = roleList.value.find((r: any) => r.role_code === target.u_role_code);
  
  // AuthUser 형식으로 변환
  const targetUser = {
    idx: target.u_idx,
    id: target.u_id,
    name: target.u_name,
    email: target.u_email || '',
    phone: target.u_phone || '',
    orgCode: target.u_org_code || '',
    orgName: target.org_name || '',
    orgLevel: target.org_level || '',
    roleCode: target.u_role_code,
    roleName: role?.role_name || target.role_name || '',
    roleLevel: role?.role_level || target.role_level || 0,
    userType: 'user' as const,
    lastLogin: target.u_last_login
  };
  
  store.dispatch('impersonate', targetUser);
  
  // 페이지 새로고침하여 권한에 맞는 데이터 로드
  router.go(0);
};

onMounted(() => {
  fetchUserList();
  fetchRoleList();
});
</script>
